import { useNavigate } from "react-router-dom";
import { Logo, LOGO_URL } from "@/components/Logo";
import { EroiSection } from "@/components/EroiSection";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Compass, GraduationCap, Trophy, Cpu, Landmark, Users2,
  ArrowRight, ArrowUpRight, Sparkles, Handshake, Layers, Globe2,
  Target, Eye, Gem, Building2, TrendingUp, Mail,
} from "lucide-react";

const NETWORK_BG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBuZXR3b3JrJTIwbGluZXN8ZW58MHx8fHwxNzgzODA5MTUxfDA&ixlib=rb-4.1.0&q=85";
const SKYSCRAPER =
  "https://images.unsplash.com/photo-1512403754473-27835f7b9984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lzY3JhcGVyJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MzgwOTE1MXww&ixlib=rb-4.1.0&q=85";
const MEETING =
  "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBzdHJhdGVnaWMlMjBtZWV0aW5nfGVufDB8fHx8MTc4MzgwOTE1MXww&ixlib=rb-4.1.0&q=85";

const CONTACT_EMAIL = "geral@alliance4growth.pt";

const PURPOSE = [
  { icon: Target, t: "Missão", d: "Acelerar o crescimento de pessoas e organizações através de alianças estratégicas e tecnologia." },
  { icon: Eye, t: "Visão", d: "Ser o ecossistema de referência para o crescimento de equipas comerciais, em qualquer setor." },
  { icon: Gem, t: "Valores", d: "Colaboração, excelência, transparência e criação de valor conjunto." },
];

const UNITS = [
  { id: "consulting", name: "A4G Consulting", icon: Compass, active: false, desc: "Consultoria estratégica de crescimento e transformação comercial." },
  { id: "academy", name: "A4G Academy", icon: GraduationCap, active: false, desc: "Auto-educação, certificações e desenvolvimento de líderes e equipas." },
  { id: "league", name: "A4G League", icon: Trophy, active: true, desc: "Plataforma de performance, cultura e crescimento para redes comerciais." },
  { id: "technology", name: "A4G Technology", icon: Cpu, active: false, desc: "Produtos digitais white-label para escalar operações e integrações." },
  { id: "capital", name: "A4G Capital", icon: Landmark, active: false, desc: "Investimento e parcerias para acelerar projetos de alto potencial." },
  { id: "community", name: "A4G Community", icon: Users2, active: false, desc: "Rede de líderes, eventos e partilha de conhecimento." },
];

const SECTORS = ["Imobiliário", "Seguros", "Automóvel", "Banca", "Retalho", "Franchising"];

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Portal() {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#070B14] text-white font-satoshi overflow-x-hidden selection:bg-[#F7861F] selection:text-[#070B14]">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 h-16 border-b border-white/10 bg-[#070B14]/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-full px-5 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="portal-logo-home">
            <Logo className="h-9" withText />
          </button>
          <nav className="hidden md:flex items-center gap-9 text-sm text-white/60">
            <button onClick={() => scrollTo("sobre")} className="hover:text-white transition-colors" data-testid="nav-about">Sobre</button>
            <button onClick={() => scrollTo("ecossistema")} className="hover:text-white transition-colors" data-testid="nav-ecosystem">Ecossistema</button>
            <button onClick={() => scrollTo("aliancas")} className="hover:text-white transition-colors" data-testid="nav-alliances">Alianças</button>
            <button onClick={() => scrollTo("contactos")} className="hover:text-white transition-colors" data-testid="nav-contacts">Contactos</button>
          </nav>
          <Button onClick={() => scrollTo("contactos")} data-testid="nav-contact-cta"
            className="bg-[#F7861F] hover:bg-[#E67312] text-[#070B14] rounded-full font-semibold px-5 h-10 focus:ring-2 focus:ring-[#F7861F]">
            Agendar reunião
          </Button>
        </div>
      </header>

      {/* HERO — institucional */}
      <section className="relative pt-32 pb-24 px-5">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#1A4FCC]/25 blur-[140px]" />
          <div className="absolute top-20 right-0 h-[420px] w-[420px] rounded-full bg-[#F7861F]/10 blur-[130px]" />
        </div>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-7">
              <Sparkles size={14} className="text-[#F7861F]" />
              <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/70">Holding de crescimento</span>
            </div>
            <h1 className="font-display font-bold tracking-tighter leading-[0.98] text-5xl md:text-6xl lg:text-7xl">
              Aceleramos o <span className="text-[#3B6EFF]">crescimento</span> através de{" "}
              <span className="text-[#F7861F]">alianças estratégicas</span>.
            </h1>
            <p className="mt-7 text-lg text-white/65 max-w-xl leading-relaxed">
              A <strong className="text-white">Alliance 4 Growth</strong> é um ecossistema que integra consultoria,
              auto-educação, tecnologia, capital e comunidade — para elevar pessoas, equipas e organizações a um novo
              patamar de desempenho.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => scrollTo("ecossistema")} data-testid="hero-cta-ecosystem"
                className="bg-[#F7861F] hover:bg-[#E67312] text-white rounded-full font-bold text-base px-7 h-12 focus:ring-2 focus:ring-[#F7861F]">
                Conhecer o ecossistema <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollTo("contactos")} data-testid="hero-cta-contact"
                className="border-white/15 bg-transparent hover:bg-white/5 text-white rounded-full font-semibold h-12 px-7">
                Falar connosco
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={NETWORK_BG} alt="Rede de crescimento Alliance 4 Growth" className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-[#070B14]/30 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-2xl bg-[#070B14]/60 backdrop-blur-md border border-white/10 p-6">
                  <img src={LOGO_URL} alt="Alliance 4 Growth" className="h-24 w-auto object-contain" />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-xl bg-[#0C1222] border border-white/10 px-5 py-3 shadow-xl">
              <p className="font-display font-bold text-2xl text-[#F7861F]">6</p>
              <p className="text-xs text-white/60 -mt-1">unidades de negócio</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOBRE — manifesto + missão/visão/valores */}
      <section id="sobre" className="py-24 px-5 bg-[#0C1222] border-y border-white/10 scroll-mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.24em] uppercase font-semibold text-[#F7861F]">Quem somos</span>
            <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight mt-4 leading-tight">
              Mais do que uma empresa. Uma aliança para crescer.
            </h2>
            <p className="text-white/60 mt-5 text-lg leading-relaxed">
              A Alliance 4 Growth nasce da convicção de que o crescimento sustentável se constrói com colaboração,
              método e tecnologia. Somos uma marca guarda-chuva que reúne várias unidades de negócio sob uma
              identidade única e escalável.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {PURPOSE.map((p, i) => (
              <motion.div key={p.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                className="rounded-2xl p-7 bg-[#121A30] border border-white/10">
                <div className="h-12 w-12 rounded-xl bg-[#1A4FCC]/15 flex items-center justify-center">
                  <p.icon size={22} className="text-[#3B6EFF]" />
                </div>
                <h3 className="font-display font-semibold text-xl mt-5">{p.t}</h3>
                <p className="text-sm text-white/55 mt-2 leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EroiSection variant="portal" />

      {/* ECOSSISTEMA — 6 unidades */}
      <section id="ecossistema" className="py-24 px-5 scroll-mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="text-xs tracking-[0.24em] uppercase font-semibold text-[#F7861F]">O ecossistema</span>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mt-3">As nossas unidades de negócio</h2>
            <p className="text-white/60 mt-4 text-lg leading-relaxed">
              Cada unidade é uma peça do ecossistema. Começámos pela tecnologia — a A4G League já está em atividade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {UNITS.map((u, i) => (
              <motion.div key={u.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                className={`group relative rounded-2xl p-7 border transition-all duration-200 hover:-translate-y-1 ${
                  u.active ? "bg-[#121A30] border-[#F7861F]/40 hover:border-[#F7861F]" : "bg-[#0C1222] border-white/10 hover:border-white/20"
                }`}
                data-testid={`unit-card-${u.id}`}>
                {u.active && <span className="absolute top-0 inset-x-7 h-0.5 bg-[#F7861F] rounded-full shadow-[0_0_18px_2px_rgba(247,134,31,0.6)]" />}
                <div className="flex items-start justify-between">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${u.active ? "bg-[#F7861F]/15" : "bg-white/5"}`}>
                    <u.icon size={22} className={u.active ? "text-[#F7861F]" : "text-white/60"} />
                  </div>
                  {u.active ? (
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F7861F] bg-[#F7861F]/10 border border-[#F7861F]/30 rounded-full px-2.5 py-1">Ativo</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">Em breve</span>
                  )}
                </div>
                <h3 className="font-display font-semibold text-xl mt-5">{u.name}</h3>
                <p className="text-sm text-white/55 mt-2 leading-relaxed">{u.desc}</p>
                {u.active ? (
                  <button onClick={() => navigate("/league")} data-testid={`unit-cta-${u.id}`}
                    className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[#F7861F] hover:gap-2 transition-all">
                    Conhecer a League <ArrowUpRight size={16} />
                  </button>
                ) : (
                  <button onClick={() => toast("Em breve", { description: `${u.name} estará disponível em breve.` })}
                    data-testid={`unit-cta-${u.id}`}
                    className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-white/50 hover:text-white transition-colors">
                    Saber mais <ArrowUpRight size={15} />
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ALIANÇAS — setores + parcerias/investidores */}
      <section id="aliancas" className="py-24 px-5 bg-[#0C1222] border-y border-white/10 scroll-mt-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs tracking-[0.24em] uppercase font-semibold text-[#F7861F]">Alianças estratégicas</span>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mt-3">
              Construído para redes de alto desempenho
            </h2>
            <p className="text-white/60 mt-4 text-lg leading-relaxed">
              Trabalhamos com redes e organizações que querem elevar a performance das suas equipas. Hoje no
              imobiliário — amanhã em qualquer setor com equipas comerciais.
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {SECTORS.map((s) => (
                <span key={s} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
                  <Building2 size={14} className="text-[#3B6EFF]" /> {s}
                </span>
              ))}
            </div>
          </div>
          <div className="grid gap-5">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
              className="rounded-2xl p-7 bg-[#121A30] border border-white/10">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#1A4FCC]/15 flex items-center justify-center"><Handshake size={20} className="text-[#3B6EFF]" /></div>
                <h3 className="font-display font-semibold text-lg">Redes & Parceiros</h3>
              </div>
              <p className="text-sm text-white/55 mt-3 leading-relaxed">
                Leve a plataforma à sua rede com identidade própria — logótipo, cores, domínio e regras à medida.
              </p>
              <button onClick={() => scrollTo("contactos")} data-testid="partner-cta"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#F7861F] hover:gap-2 transition-all">
                Tornar-se parceiro <ArrowUpRight size={16} />
              </button>
            </motion.div>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
              className="rounded-2xl p-7 bg-[#121A30] border border-white/10">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#F7861F]/15 flex items-center justify-center"><TrendingUp size={20} className="text-[#F7861F]" /></div>
                <h3 className="font-display font-semibold text-lg">Investidores</h3>
              </div>
              <p className="text-sm text-white/55 mt-3 leading-relaxed">
                Junte-se à expansão de um ecossistema modular, escalável e preparado para múltiplos setores.
              </p>
              <button onClick={() => scrollTo("contactos")} data-testid="investor-cta"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#F7861F] hover:gap-2 transition-all">
                Falar sobre investimento <ArrowUpRight size={16} />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LEAGUE — callout de produto (conciso) */}
      <section className="py-24 px-5 scroll-mt-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1 rounded-2xl overflow-hidden border border-white/10">
            <img src={SKYSCRAPER} alt="Redes imobiliárias" className="w-full h-full max-h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-transparent to-transparent" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-xs tracking-[0.24em] uppercase font-semibold text-[#F7861F]">Unidade em atividade</span>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mt-3">A4G League</h2>
            <p className="text-white/60 mt-4 text-lg leading-relaxed">
              A nossa plataforma white-label de <strong className="text-white">performance, cultura e crescimento</strong>{" "}
              para redes comerciais. Um produto próprio, com página e experiência dedicadas.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigate("/league")} data-testid="league-know-more"
                className="bg-[#1A4FCC] hover:bg-[#163E9F] text-white rounded-full font-bold h-12 px-7 focus:ring-2 focus:ring-[#F7861F]">
                Conhecer a A4G League <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollTo("contactos")} data-testid="league-contact"
                className="border-white/15 bg-transparent hover:bg-white/5 text-white rounded-full font-semibold h-12 px-7">
                Falar connosco
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* VISÃO */}
      <section id="visao" className="py-24 px-5 bg-[#0C1222] border-y border-white/10 scroll-mt-16">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs tracking-[0.24em] uppercase font-semibold text-[#F7861F]">A visão a longo prazo</span>
          <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mt-3">Um ecossistema para múltiplos setores</h2>
          <p className="text-white/60 mt-5 text-lg leading-relaxed">
            Desenhamos a Alliance 4 Growth para escalar. A mesma base tecnológica que hoje serve o imobiliário poderá,
            amanhã, servir seguros, automóvel, banca, retalho ou franchising — dando forma ao{" "}
            <strong className="text-white">Alliance 4 Growth OS</strong>.
          </p>
        </div>
      </section>

      {/* CONTACTOS */}
      <section id="contactos" className="py-24 px-5 scroll-mt-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
            <Handshake size={14} className="text-[#F7861F]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/70">Vamos crescer juntos</span>
          </div>
          <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight">
            Parcerias, redes e investimento
          </h2>
          <p className="text-white/60 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
            Quer levar a Alliance 4 Growth à sua rede, tornar-se parceiro ou investir no ecossistema? Fale connosco.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-btn">
              <Button size="lg" className="bg-[#F7861F] hover:bg-[#E67312] text-white rounded-full font-bold h-12 px-8 focus:ring-2 focus:ring-[#F7861F]">
                <Mail size={18} className="mr-2" /> {CONTACT_EMAIL}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="pt-16 pb-12 px-5 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-[1.4fr_1fr_1fr] gap-12">
            <div>
              <Logo className="h-9" withText />
              <p className="text-sm text-white/50 mt-4 max-w-sm leading-relaxed">
                Ecossistema de crescimento. Alianças estratégicas para acelerar equipas e organizações.
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Unidades</p>
              <ul className="space-y-2 text-sm">
                {UNITS.map((u) => (
                  <li key={u.id} className="flex items-center gap-2 text-white/70">
                    {u.name}
                    {!u.active && <span className="text-[9px] uppercase tracking-wider text-white/35 border border-white/10 rounded px-1.5 py-0.5">Em breve</span>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Contactos</p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-white/70 hover:text-white transition-colors" data-testid="footer-email">{CONTACT_EMAIL}</a>
            </div>
          </div>
          <div className="mt-16 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-display font-bold tracking-tighter text-2xl text-white/15">ALLIANCE 4 GROWTH</p>
            <p className="text-xs text-white/40">© {new Date().getFullYear()} Alliance 4 Growth · Todos os direitos reservados</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
